import React from 'react';
import type { FieldValues, UseFormReturn, Validate } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { Theme } from '@mui/material';
import { Autocomplete, CircularProgress, FormControl, Paper, TextField } from '@mui/material';
import { Box } from '@mui/system';

import { Container, ContainerItem, Error, InputLabel } from 'ui';
import { theme } from 'ui/theme';
import palette from 'ui/theme/palette';
import { errorMessages } from 'util/forms.util';

export interface AutocompleteOption {
  label: string;
  value: string;
  optionLabel: string;
}
interface ValidationRule<T> {
  message: string;
  value: T;
}
interface Props {
  label: string;
  open: boolean;
  name: string;
  form: UseFormReturn<FieldValues>;
  isLoading: boolean;
  optionsData: AutocompleteOption[];
  handleClose: () => void;
  onParentChange: (selectedValue: AutocompleteOption) => void;
  onSearchChange: (value: string) => void;
  onParentBlur: () => void;
  handleOpen?: () => void;
  dataCy?: string;
  rules?: {
    required?: ValidationRule<boolean>;
    maxLength?: ValidationRule<number>;
    minLength?: ValidationRule<number>;
    max?: ValidationRule<number>;
    min?: ValidationRule<number>;
    pattern?: ValidationRule<RegExp>;
    validate?:
      | Validate<string | number, FieldValues>
      | Record<string, Validate<string | number, FieldValues>>;
  };
  optionIcon?: React.ReactNode;
  defaultValue?: string;
  disabled?: boolean;
  readOnly?: boolean;
  viewOnly?: boolean;
}

export const InputAutocomplete: React.FC<Props> = ({
  label,
  open,
  name,
  form,
  isLoading,
  optionsData,
  handleClose,
  onParentChange,
  onSearchChange,
  onParentBlur,
  handleOpen,
  dataCy,
  rules,
  optionIcon,
  defaultValue,
  disabled,
  readOnly,
  viewOnly
}) => {
  return (
    <FormControl fullWidth data-cy={dataCy}>
      <Container padding={0} width="100%" align="center">
        <ContainerItem padding={0} align="center" flex margin="0 0 6px 0">
          <InputLabel
            dataCy={'input-label'}
            label={label}
            name={name}
            required={viewOnly ? false : rules?.required?.value}
            readOnly={readOnly}
            viewOnly={viewOnly}
            rules={rules}
          />
        </ContainerItem>
      </Container>
      <Controller
        control={form.control}
        name={name}
        rules={rules}
        render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => {
          const refinedValue =
            value === null || value === undefined ? '' : value?.value ? value.value : value;
          const refinedLabel =
            value === null || value === undefined ? '' : value?.label ? value.label : value;
          const refinedOptionLabel = value === null ? '' : value?.optionLabel ? value.optionLabel : value;
          return (
            <>
              <Autocomplete
                openOnFocus={false}
                open={open}
                onOpen={handleOpen}
                onClose={handleClose}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                  onParentBlur();
                  onChange(event.target?.value);
                }}
                onChange={(event, value: any) => {
                  if (value) {
                    onParentChange(value);
                  }
                  onChange(value?.label !== undefined ? value.label : value);
                }}
                // This allows the user to type in a value that is not in the options
                freeSolo
                // This makes sure that when the user types an input we set it as the selected value for the form
                autoSelect
                defaultValue={{
                  value: refinedValue,
                  label: refinedLabel,
                  optionLabel: refinedOptionLabel
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option: any) => {
                  // if the option has a label it's a selected value from the dropdown, if it doesn't its a free solo user input value
                  return option.label !== undefined ? option.label : option;
                }}
                options={optionsData.length ? (optionsData as any) : []}
                filterOptions={(x) => x}
                loading={isLoading}
                disabled={disabled || readOnly || viewOnly}
                size="small"
                PaperComponent={(props) => (
                  <Paper
                    elevation={4}
                    {...props}
                    sx={{
                      margin: '6px 0',
                      borderRadius: '6px',
                      '& ul': {
                        maxHeight: '13rem',
                        '& .MuiAutocomplete-option.Mui-focused': {
                          backgroundColor: palette.primary.screen
                        }
                      },
                      '.MuiAutocomplete-listbox .MuiAutocomplete-option': {
                        padding: '10px 12px'
                      }
                    }}
                  />
                )}
                disablePortal={true}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.value}>
                      {optionIcon && <Box sx={{ marginRight: '8px' }}>{optionIcon}</Box>}
                      {option.optionLabel}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: isLoading && <CircularProgress color="primary" size={20} />
                      }
                    }}
                    error={error ? true : false}
                    onChange={(event) => onSearchChange(event.target.value)}
                  />
                )}
                sx={styles(theme, palette, viewOnly, readOnly, disabled, error)}
              />
              <Error
                content={error ? `* ${label} ${error.message || errorMessages()[error.type]}` : ''}
                dataCy="validation-message"
              />
            </>
          );
        }}
      />
    </FormControl>
  );
};

const styles = (
  theme: Theme,
  palette: any,
  viewOnly: boolean | undefined,
  readOnly: boolean | undefined,
  disabled: boolean | undefined,
  error: any
) => ({
  mt: 0,
  '& .MuiInputBase-root': {
    background: viewOnly ? 'transparent' : theme.palette.background.default,
    borderRadius: '5px',
    color: theme.palette.text.primary,
    padding: viewOnly ? '1px 0 5px' : '0.5rem 0.75rem',
    cursor: readOnly || disabled ? 'not-allowed' : 'default',

    '&.Mui-disabled': {
      color: theme.palette.text.secondary,
      WebkitTextFillColor: 'inherit'
    },
    '&::placeholder': {
      color: theme.palette.text.disabled
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: '1px solid',
      borderColor: viewOnly ? 'transparent' : error ? palette.error.main : theme.palette.divider
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: viewOnly ? 'transparent' : error ? palette.error.main : theme.palette.divider
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: viewOnly ? 'transparent' : error ? palette.error.main : palette.primary.border,
      boxShadow: error ? `0px 0px 0px 3px rgba(255, 78, 80, 0.25)` : `0px 0px 0px 3px rgba(3, 166, 91, 0.25)`
    },
    '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
      cursor: readOnly || disabled ? 'not-allowed' : 'default'
    },
    input: {
      cursor: readOnly || disabled ? 'not-allowed' : 'default'
    }
  },
  '.Mui-disabled.MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
    borderColor: viewOnly ? 'transparent' : error ? palette.error.main : theme.palette.divider
  },
  '.MuiAutocomplete-endAdornment': {
    display: viewOnly ? 'none' : 'block'
  },
  '&.Mui-focused .MuiInputBase-root.MuiInputBase-adornedEnd': {
    borderColor: viewOnly ? 'transparent' : error ? palette.error.main : palette.primary.border,
    color: theme.palette.text.primary,
    cursor: readOnly || disabled ? 'not-allowed' : 'default'
  }
});
